import axios from 'axios';
import getFirebaseAuthToken from './fireBaseAuthenticationService';
import { BACKEND_URL, TIMESHEET_POST_TIMEOUT } from '../utils/constants';
import useUserStore from '../stores/userStore';
import { loggedAlert } from './eventService';
import { backgroundSafeJsonPost } from '../utils/backgroundSafeFetch';

const LONG_TIMEOUT = 180000; // 3 minutes for document generation
const PAYROLL_RUN_TIMEOUT = 120000;

async function withKeepAwake(work) {
    useUserStore.getState().beginKeepAwake();
    try {
        return await work();
    } finally {
        useUserStore.getState().endKeepAwake();
    }
}

/**
 * Fetch available pay periods (last completed, current, next)
 */
export const fetchPayPeriods = async () => {
    try {
        const token = await getFirebaseAuthToken();
        
        const response = await axios.get(`${BACKEND_URL}/payroll/pay_periods`, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
            timeout: TIMESHEET_POST_TIMEOUT
        });

        return response.data;
    } catch (error) {
        console.error("Error fetching pay periods:", JSON.stringify(error.toJSON?.() || error));
        const errorMessage = error?.response?.data?.error || error?.message || "Unknown error";
        throw new Error(`Failed to fetch pay periods: ${errorMessage}`);
    }
};

/**
 * Check if the current user has access to payroll features
 */
export const checkPayrollAccess = async (userId) => {
    try {
        const token = await getFirebaseAuthToken();
        
        const response = await axios.post(`${BACKEND_URL}/payroll/check_access`, 
            { user_id: userId },
            {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
                timeout: TIMESHEET_POST_TIMEOUT
            }
        );

        return response.data.has_access;
    } catch (error) {
        console.error("Error checking payroll access:", JSON.stringify(error.toJSON?.() || error));
        return false;
    }
};

/**
 * Check for missing timesheet events (incomplete clock in/out pairs)
 * Returns { has_issues: boolean, issues: [...] }
 */
export const checkMissingEvents = async (startDate, endDate, userId) => {
    const { currentUser, isUserAuthenticated } = useUserStore.getState();

    if (!isUserAuthenticated) {
        loggedAlert("checkMissingEvents: Please wait for authentication to complete.", currentUser?.user_id);
        return null;
    }

    try {
        const token = await getFirebaseAuthToken();
        return await withKeepAwake(() => backgroundSafeJsonPost(
            `${BACKEND_URL}/payroll/check_missing_events`,
            {
                start_date: startDate,
                end_date: endDate,
                user_id: userId
            },
            {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
                timeoutMs: TIMESHEET_POST_TIMEOUT
            }
        ));
    } catch (error) {
        console.error("Error checking missing events:", JSON.stringify(error.toJSON?.() || error));
        const errorMessage = error?.response?.data?.error || error?.message || "Unknown error";
        throw new Error(`Failed to check missing events: ${errorMessage}`);
    }
};

/**
 * Preview payroll for a period (without saving)
 */
export const previewPayroll = async (startDate, endDate, userId) => {
    const { currentUser, isUserAuthenticated } = useUserStore.getState();

    if (!isUserAuthenticated) {
        loggedAlert("previewPayroll: Please wait for authentication to complete.", currentUser?.user_id);
        return null;
    }

    try {
        const token = await getFirebaseAuthToken();
        return await withKeepAwake(() => backgroundSafeJsonPost(
            `${BACKEND_URL}/payroll/preview`,
            {
                start_date: startDate,
                end_date: endDate,
                user_id: userId
            },
            {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
                timeoutMs: PAYROLL_RUN_TIMEOUT
            }
        ));
    } catch (error) {
        console.error("Error previewing payroll:", JSON.stringify(error.toJSON?.() || error));
        const errorMessage = error?.response?.data?.error || error?.message || "Unknown error";
        throw new Error(`Failed to preview payroll: ${errorMessage}`);
    }
};

/**
 * Run payroll for a period and save to BigQuery
 */
export const runPayroll = async (startDate, endDate, userId, saveToBigQuery = true) => {
    const { currentUser, isUserAuthenticated } = useUserStore.getState();

    if (!isUserAuthenticated) {
        loggedAlert("runPayroll: Please wait for authentication to complete.", currentUser?.user_id);
        return null;
    }

    try {
        const token = await getFirebaseAuthToken();
        return await withKeepAwake(() => backgroundSafeJsonPost(
            `${BACKEND_URL}/payroll/run`,
            {
                start_date: startDate,
                end_date: endDate,
                user_id: userId,
                save_to_bigquery: saveToBigQuery
            },
            {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
                timeoutMs: PAYROLL_RUN_TIMEOUT
            }
        ));
    } catch (error) {
        console.error("Error running payroll:", JSON.stringify(error.toJSON?.() || error));
        const errorMessage = error?.response?.data?.error || error?.message || "Unknown error";
        throw new Error(`Failed to run payroll: ${errorMessage}`);
    }
};

/**
 * Generate PDF documents for a payroll run and store in GCS
 */
export const generatePayrollDocuments = async (runId, employeeData, periodStart, periodEnd, userId, rawTimesheetData = null) => {
    try {
        const token = await getFirebaseAuthToken();
        
        return await withKeepAwake(() => backgroundSafeJsonPost(
            `${BACKEND_URL}/payroll/generate_documents`,
            {
                user_id: userId,
                run_id: runId,
                employee_data: employeeData,
                period_start: periodStart,
                period_end: periodEnd,
                raw_timesheet_data: rawTimesheetData
            },
            {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
                timeoutMs: LONG_TIMEOUT
            }
        ));
    } catch (error) {
        console.error("Error generating payroll documents:", JSON.stringify(error.toJSON?.() || error));
        const errorMessage = error?.response?.data?.error || error?.message || "Unknown error";
        throw new Error(`Failed to generate documents: ${errorMessage}`);
    }
};

/**
 * Get a signed URL for downloading a payroll PDF
 */
export const getPayrollPdfDownloadUrl = async (gcsPath, userId) => {
    try {
        const token = await getFirebaseAuthToken();
        
        const response = await axios.post(`${BACKEND_URL}/payroll/download_pdf`, 
            {
                user_id: userId,
                gcs_path: gcsPath
            },
            {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
                timeout: TIMESHEET_POST_TIMEOUT
            }
        );

        return response.data.download_url;
    } catch (error) {
        console.error("Error getting PDF download URL:", JSON.stringify(error.toJSON?.() || error));
        const errorMessage = error?.response?.data?.error || error?.message || "Unknown error";
        throw new Error(`Failed to get download URL: ${errorMessage}`);
    }
};

/**
 * Email a single payroll PDF to an employee
 */
export const emailPayrollPdf = async (gcsPath, recipientEmail, employeeName, periodStart, periodEnd, userId, ccEmails = []) => {
    try {
        const token = await getFirebaseAuthToken();
        
        const response = await axios.post(`${BACKEND_URL}/payroll/email_pdf`, 
            {
                user_id: userId,
                gcs_path: gcsPath,
                recipient_email: recipientEmail,
                employee_name: employeeName,
                period_start: periodStart,
                period_end: periodEnd,
                cc_emails: ccEmails
            },
            {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
                timeout: TIMESHEET_POST_TIMEOUT
            }
        );

        return response.data;
    } catch (error) {
        console.error("Error emailing payroll PDF:", JSON.stringify(error.toJSON?.() || error));
        const errorMessage = error?.response?.data?.error || error?.message || "Unknown error";
        throw new Error(`Failed to send email: ${errorMessage}`);
    }
};

/**
 * Get a suggested resolution time for a missing clock event using DaySmart data.
 * Returns { suggested_time, suggestion_source, appointments }
 */
export const suggestResolution = async (userId, date, likelyMissing, locationId, requestingUserId) => {
    try {
        const token = await getFirebaseAuthToken();
        
        const response = await axios.post(`${BACKEND_URL}/payroll/suggest_resolution`, 
            {
                user_id: userId,
                date: date,
                likely_missing: likelyMissing,
                location_id: locationId,
                requesting_user_id: requestingUserId
            },
            {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
                timeout: TIMESHEET_POST_TIMEOUT
            }
        );

        return response.data;
    } catch (error) {
        console.error("Error suggesting resolution:", JSON.stringify(error.toJSON?.() || error));
        const errorMessage = error?.response?.data?.error || error?.message || "Unknown error";
        throw new Error(`Failed to get suggestion: ${errorMessage}`);
    }
};

/**
 * Insert a corrective clock-in or clock-out event to fix a timesheet issue.
 * Returns { success, event, message }
 */
export const resolveMissingEvent = async (userId, eventType, eventTimestampMs, locationId, createdBy, notes = "") => {
    try {
        const token = await getFirebaseAuthToken();
        
        const response = await axios.post(`${BACKEND_URL}/payroll/resolve_missing_event`, 
            {
                user_id: userId,
                event_type: eventType,
                event_timestamp: eventTimestampMs,
                location_id: locationId,
                created_by: createdBy,
                notes: notes
            },
            {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
                timeout: TIMESHEET_POST_TIMEOUT
            }
        );

        return response.data;
    } catch (error) {
        console.error("Error resolving missing event:", JSON.stringify(error.toJSON?.() || error));
        const errorMessage = error?.response?.data?.error || error?.message || "Unknown error";
        throw new Error(`Failed to resolve missing event: ${errorMessage}`);
    }
};

/**
 * Email payroll PDFs to all employees
 */
export const emailAllPayrollPdfs = async (documents, periodStart, periodEnd, userId, ccEmails = []) => {
    try {
        const token = await getFirebaseAuthToken();
        
        return await withKeepAwake(() => backgroundSafeJsonPost(
            `${BACKEND_URL}/payroll/email_all`,
            {
                user_id: userId,
                documents: documents,
                period_start: periodStart,
                period_end: periodEnd,
                cc_emails: ccEmails
            },
            {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
                timeoutMs: LONG_TIMEOUT
            }
        ));
    } catch (error) {
        console.error("Error emailing all payroll PDFs:", JSON.stringify(error.toJSON?.() || error));
        const errorMessage = error?.response?.data?.error || error?.message || "Unknown error";
        throw new Error(`Failed to send emails: ${errorMessage}`);
    }
};

/**
 * Fetch ESST report data for a given employee and year.
 * Returns { employee_name, user_id, year, summary, days }
 */
export const fetchEsstReport = async (userId, year = new Date().getFullYear()) => {
    try {
        const token = await getFirebaseAuthToken();

        const response = await axios.post(
            `${BACKEND_URL}/employees/esst_report`,
            {
                user_id: userId,
                year: year,
            },
            {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
                timeout: TIMESHEET_POST_TIMEOUT,
            }
        );

        return response.data;
    } catch (error) {
        console.error("Error fetching ESST report:", JSON.stringify(error.toJSON?.() || error));
        const errorMessage = error?.response?.data?.error || error?.message || "Unknown error";
        throw new Error(`Failed to fetch ESST report: ${errorMessage}`);
    }
};
